import { useCallback } from 'react';

import { noticesStore } from 'stores/global';

import { useStoreApi as useMotionEditorStoreApi } from '../../motionEditorStore';
import { resolveKeyPoseMotionContext } from '../keyPoseMotionUtils';
import { useStoreApi as useKeyPoseStoreApi } from '../keyPoseStore';

type SaveKeyPoseReason =
  | 'add-key-pose'
  | 'paste-key-pose'
  | 'remove-key-pose'
  | 'remove-key-poses'
  | 'edit-key-pose'
  | 'undo-key-pose'
  | 'redo-key-pose';

interface SaveCurrentKeyPosesOptions {
  referenceName?: string | null;
  signal?: AbortSignal;
}

export function useKeyPosePersistenceActions() {
  const motionEditorStore = useMotionEditorStoreApi();
  const keyPoseStore = useKeyPoseStoreApi();

  const saveCurrentKeyPoses = useCallback(
    async (
      reason: SaveKeyPoseReason,
      { referenceName, signal }: SaveCurrentKeyPosesOptions = {},
    ) => {
      if (!keyPoseStore.state.hasApiKey) {
        noticesStore.error('Failed to authenticate. Please try again.');
        return false;
      }

      if (!motionEditorStore.state.scene.data) {
        noticesStore.error('Load a scene before saving key poses.');
        return false;
      }

      const context = resolveKeyPoseMotionContext({
        scene: motionEditorStore.state.scene,
        playback: motionEditorStore.state.playback,
        keyPoseReferenceName:
          referenceName ?? keyPoseStore.state.activeReferenceName,
      });
      if (!context) {
        noticesStore.error('Select a motion before saving key poses.');
        return false;
      }

      const didSave = await keyPoseStore.saveCurrentSceneObjectMotionKeyPoses({
        sceneID: context.sceneID,
        referenceName: context.referenceName,
        timelineIndex: context.timelineIndex,
        signal,
      });

      if (!didSave) {
        noticesStore.error(getSaveFailureMessage(reason));
      }

      return didSave;
    },
    [keyPoseStore, motionEditorStore],
  );

  return { saveCurrentKeyPoses };
}

function getSaveFailureMessage(reason: SaveKeyPoseReason) {
  switch (reason) {
    case 'add-key-pose':
      return 'Could not save the new key pose.';
    case 'paste-key-pose':
      return 'Could not save the pasted key pose.';
    case 'remove-key-pose':
      return 'Could not save the removed key pose.';
    case 'remove-key-poses':
      return 'Could not save the removed key poses.';
    case 'edit-key-pose':
      return 'Could not save the edited key pose.';
    case 'undo-key-pose':
      return 'Could not save the undone key pose edit.';
    case 'redo-key-pose':
      return 'Could not save the redone key pose edit.';
  }
}
